import { useMemo } from 'react';
import { MenuItem } from '@mui/material';
import MeetingService from 'services/MeetingService';
import {
  ActivityType,
  ApprovalLevelType,
  CommentActionType,
  CommentCaseType,
  CommentType,
  IPCheckMotionType,
  FinalActionType,
  IPCheckType,
  PersuasiveType,
  RelatedType,
  SafetyMotionType,
  SignificantType,
  WithdrawalType,
} from 'store/enums/BallotReviewTypes';
import { VoteValueType, VotingRuleType } from 'store/enums/VoteTypes';
import { ActivityLog, Attendee, MeetingSession, Motion } from 'store/types/Meeting';

export const previewQueryParameter = 'preview';
export const activityLogQueryParameter = 'log';

export const defaultMotions: Motion[] = [
  {
    id: 'motion-1',
    title: 'Abandon SNARF Document #',
    statement: 'Abandon SNARF Document #',
  },
  {
    id: 'motion-2',
    title: 'Cancel SNARF Document #',
    statement: 'Cancel SNARF Document #',
  },
  {
    id: 'motion-3',
    title: 'Approve the minutes as written',
    statement: 'Approve the minutes as written',
  },
  {
    id: 'motion-4',
    title: 'Approve the SNARF',
    statement: 'Approve the SNARF',
  },
  {
    id: 'motion-5',
    title: 'Approve the TF',
    statement: 'Approve the TF',
  },
  {
    id: 'motion-6',
    title: 'Authorize the Document for Letter Ballot',
    statement: 'Authorize the Document for Letter Ballot',
  },
  {
    id: 'motion-7',
    title: 'Approve a 1 year extension of the project period for the SNARF',
    statement: 'Approve a 1 year extension of the project period for the SNARF',
  },
  {
    id: 'motion-8',
    title: 'Disband the TF',
    statement: 'Disband the TF',
  },
];

export const iPCheckMotionId = 'ip-check-motion';

export const votingRuleTypeLabel: Record<VotingRuleType, string> = {
  [VotingRuleType.SimpleMajority]: 'Majority',
  [VotingRuleType.OneThirdMajority]: '> 1/3',
  [VotingRuleType.TwoThirdsMajority]: '>= 2/3',
};

export const voteValueLabels: Record<VoteValueType, string> = {
  [VoteValueType.Yes]: 'In Favor',
  [VoteValueType.No]: 'Against',
  [VoteValueType.Abstain]: 'Abstain',
};

export const voteValueColor: Record<VoteValueType, 'success' | 'error' | 'warning'> = {
  [VoteValueType.Yes]: 'success',
  [VoteValueType.No]: 'error',
  [VoteValueType.Abstain]: 'warning',
};

export const ipCheckValueColor: Record<VoteValueType, 'success' | 'error' | 'warning'> = {
  [VoteValueType.Yes]: 'error',
  [VoteValueType.No]: 'success',
  [VoteValueType.Abstain]: 'warning',
};

export const withdrawalTypeLabel: Record<WithdrawalType, string> = {
  [WithdrawalType.NoWithdrawal]: 'No Negative withdrawal made by Voter.',
  [WithdrawalType.WithdrawalMade]: 'Withdrawal document received by Standards staff',
};

export const commentTypeLabel: Record<CommentType, string> = {
  [CommentType.VoterComment]: `Voter's Comment`,
  [CommentType.RejectHandlingComment]: 'Comments Created by Handling Negative',
};

export const commentActionTypeLabel: Record<CommentActionType, string> = {
  [CommentActionType.AlreadyAddressed]: 'Already addressed by Commenter #, Comment #',
  [CommentActionType.NoFurtherAction]: 'No further action was taken by the TC Chapter.',
  [CommentActionType.ReferToTaskForce]: 'Refer to the TF for more consideration.',
  [CommentActionType.NewBusiness]: 'New Business',
  [CommentActionType.EditorialChange]: 'Editorial Change',
};

export const commentCaseTypeLabel: Record<CommentCaseType, string> = {
  [CommentCaseType.NoVote]: 'Case 1: No vote in this section',
  [CommentCaseType.Voted]: 'Case 2: Voted in this section',
};

export const relatedTypeLabel: Record<RelatedType, string> = {
  [RelatedType.Related]: '"Related" is mutually agreed upon. (Needs no motion.)',
  [RelatedType.NotRelated]: 'Negative is not related. (Needs ≥ 2/3 votes to pass.)',
};

export const persuasiveTypeLabel: Record<PersuasiveType, string> = {
  [PersuasiveType.Persuasive]: 'Negative is related and persuasive. (Needs > 1/3 votes to pass.)',
  [PersuasiveType.NotPersuasive]: 'Negative is related and not persuasive. (Needs ≥ 2/3 votes to pass.)',
};

export const significantTypeLabel: Record<SignificantType, string> = {
  [SignificantType.Significant]: 'It is mutually agreed upon to term the Negative "significant".',
  [SignificantType.NotSignificant]: 'It is mutually agreed upon to term the Negative "not significant".',
  [SignificantType.VoteToDetermine]: 'Whether or not the Negative is "not significant" is decided by a vote.',
};

export const approvalLevelTypeLabel: Record<ApprovalLevelType, string> = {
  [ApprovalLevelType.GloballyApproved]:
    'Globally Approved (No Ratification Ballot needed): The Letter Ballot meets the Letter Ballot ' +
    'approval conditions for the global technical committee.',
  [ApprovalLevelType.NeedsRatification]:
    'Need a Ratification Ballot: The Letter Ballot meets the Letter Ballot approval conditions ' +
    'for the TC Chapter and a Ratification Ballot will be issued to validate technical changes.',
};

export const safetyMotionTypeLabel: Record<SafetyMotionType, string> = {
  [SafetyMotionType.SafetyDocument]:
    '<strong>This is a Safety Document</strong>, when all safety-related information is removed, ' +
    'the Document is not technically sound and complete. (Regulations ¶ 8.7.2)',
  [SafetyMotionType.NotSafetyDocument]:
    '<strong>This is not a Safety Document</strong>, when all safety-related information is removed, ' +
    'the Document is still technically sound and complete. (Regulations ¶ 8.7.1)',
};

export const ipCheckTypeLabel: Record<IPCheckType, string> = {
  [IPCheckType.Affirmative]: 'The question is answered in affirmative',
  [IPCheckType.NotAffirmative]:
    'The question is NOT answered in affirmative (No potentially material patented technology or use/reproduction of copyrighted items/trademarks is known.)',
};

export const ipCheckMotionTypeLabel: Record<IPCheckMotionType, string> = {
  [IPCheckMotionType.AskPermission]: 'Ask ISC for special permission to publish.',
  [IPCheckMotionType.QuitActivity]: 'Quit activity.',
  [IPCheckMotionType.Wait]: 'Wait for copyright release letter',
};

export const finalActionTypeLabel: Record<FinalActionType, string> = {
  [FinalActionType.Passed]:
    'This Document passed TC Chapter review as balloted and will be forwarded to the ISC A&R SC for procedural review.',
  [FinalActionType.PassedWithEditorialChanges]:
    'This Document passed TC Chapter review with editorial changes and will be forwarded to the ISC A&R SC for procedural review.',
  [FinalActionType.PassedWithTechnicalChanges]:
    'This Document passed TC Chapter review with technical changes and with or without editorial changes and will be forwarded ' +
    'to the ISC A&R SC for procedural review. A Ratification Ballot will be issued to verify the technical changes.',
  [FinalActionType.FailedAndReturned]:
    'This Document failed TC Chapter review and will be returned to the TF for rework.',
  [FinalActionType.FailedAndDiscontinued]: 'This Document failed TC Chapter review and work will be discontinued.',
};

export const activityTypeLabel: Record<ActivityType, string> = {
  [ActivityType.Motion]: 'Motion',
  [ActivityType.Document]: 'Document',
  [ActivityType.Reject]: 'Reject',
  [ActivityType.TechnicalIssue]: 'Other Technical Issue',
  [ActivityType.Comment]: 'Comment',
  [ActivityType.IpCheck]: 'IP Check',
  [ActivityType.Approval]: 'Approval',
  [ActivityType.Connection]: 'Connection',
};

export const ipQuestion = `Are you aware of any patented technology that might be relevant (see Regulations ¶ 16.3.1.1) to the Standard
  or Safety Guideline; or, any copyrighted items or trademarks that are used/reproduced (see Regulations ¶
  16.4.1.2) in the Standard or Safety Guideline. (Also see, Regulations § 8.8)`;

const getAttendeeDisplayValue = (attendee: Attendee) => {
  const affiliate = attendee.affiliate?.trim();
  return affiliate ? `${attendee.name} (${affiliate})` : attendee.name;
};

export const excludeAttendee = (attendees: Attendee[], exclude?: string) => {
  if (!exclude) return attendees;
  return attendees.filter((a) => getAttendeeDisplayValue(a) !== exclude);
};

export const getAttendeeOptions = (attendees: Attendee[], presidingChairId?: string) =>
  attendees
    ?.filter((a) => a.affiliate?.toLowerCase() !== 'semi' && (!presidingChairId || a.id !== presidingChairId))
    .map((a) => {
      const displayValue = getAttendeeDisplayValue(a);

      return (
        <MenuItem key={a.id} value={displayValue}>
          {displayValue}
        </MenuItem>
      );
    });

export function useSafeSelectValue<T>(value: string, options: T[] | undefined, getOptionId: (option: T) => string) {
  return useMemo(() => {
    if (!value) return '';
    if (!options?.length) return '';

    return options.some((o) => getOptionId(o) === value) ? value : '';
  }, [value, options, getOptionId]);
}

export function useSafeSelectIdValue<T extends { id: string }>(value: string, options?: T[]) {
  return useSafeSelectValue(value, options, (o) => o.id);
}

export function useSafeAttendeeDisplayValue(value: string, attendees?: Attendee[]) {
  return useSafeSelectValue(value, attendees, (a) => getAttendeeDisplayValue(a));
}

export function meetingSessionToActivity(meetingSession?: MeetingSession): Partial<ActivityLog> {
  return {
    document: meetingSession?.currentDocument,
    reject: meetingSession?.currentReject,
    technicalIssue: meetingSession?.currentTechnicalIssue,
    comment: meetingSession?.currentComment,
  };
}

export const createImagesUploadHandler =
  (meetingId: string) =>
  async (blobInfo: any, progress: (percent: number) => void): Promise<string> => {
    progress(0);

    const base64 = await blobInfo.base64();
    const base64String = `data:${blobInfo.blob().type};base64,${base64}`;

    const response = await MeetingService.upload(meetingId, base64String);

    const url = (response as any).url ?? (response as any).Url;
    if (typeof url !== 'string' || url.length === 0) {
      throw new Error('Upload did not return a URL');
    }

    progress(100);
    return url;
  };

type HasId = { id: string | number };
export function upsertById<T extends HasId>(items: T[], item: T): T[] {
  const index = items.findIndex((i) => i.id === item.id);
  if (index === -1) return [...items, item];

  const next = items.slice();
  next[index] = item;
  return next;
}
